import { z } from "zod";

/** Sekunden pro Seite für den Auto-Scroll. */
export const SECS_PER_PAGE = { min: 5, max: 600, default: 45 } as const;

export const DEFAULT_CATEGORY_COLOR = "#6366F1";
export const DEFAULT_CATEGORY_ICON = "♪";
export const DEFAULT_PLAYLIST_ICON = "🎵";
export const DEFAULT_PLAYLIST_NAME = "Setlist";

export const ACCENT_PALETTE = [
  "#6366F1",
  "#D946EF",
  "#F97316",
  "#0EA5E9",
  "#22C55E",
  "#F59E0B",
  "#EC4899",
  "#14B8A6",
  "#A855F7",
  "#F43F5E",
] as const;

export function accentForIndex(index: number): string {
  const len = ACCENT_PALETTE.length;
  return ACCENT_PALETTE[((index % len) + len) % len] as string;
}

/** Normalisiert #abc / #aabbcc zu #AABBCC; null bei ungültiger Eingabe. */
export function normalizeHexColor(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(input.trim());
  if (!match?.[1]) return null;
  const value = match[1];
  const full =
    value.length === 3
      ? value
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : value;
  return `#${full.toUpperCase()}`;
}

const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

/** Erstes Graphem (z. B. ein Emoji inkl. Modifikatoren) oder null. */
export function firstGrapheme(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const first = segmenter.segment(trimmed)[Symbol.iterator]().next().value?.segment ?? null;
  if (!first || first.length > 16) return null;
  return first;
}

export const hexColorSchema = z.string().transform((value, ctx) => {
  const color = normalizeHexColor(value);
  if (!color) {
    ctx.addIssue({ code: "custom", message: "Ungültige Farbe" });
    return z.NEVER;
  }
  return color;
});

export const iconSchema = z.string().transform((value, ctx) => {
  const icon = firstGrapheme(value);
  if (!icon) {
    ctx.addIssue({ code: "custom", message: "Ungültiges Icon" });
    return z.NEVER;
  }
  return icon;
});

export const idSchema = z.uuid();

export const nameSchema = z.string().trim().min(1).max(120);

export const secsPerPageSchema = z.number().int().min(SECS_PER_PAGE.min).max(SECS_PER_PAGE.max);
